import React, { useState } from 'react'
import { Menu, MenuItem, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Maybe } from 'types/Maybe'
import { ChatHistoryItem } from 'api/queries/chat/types'
import { useDeleteChatMutation } from 'api/mutations/chat/chat'
import { EditChatModal } from 'components/modals/EditChatModal/EditChatModal'
import { DeleteModal } from 'components/modals/DeleteModal/DeleteModal'
import { useGetChatsQuery } from 'api/queries/chat/chat'
import * as Styled from './ChatHistory.styles'

interface ChatHistoryProps {
  chatId?: string
  onNavigate: () => void
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ chatId, onNavigate }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { data: chats = [], isSuccess } = useGetChatsQuery()

  const [menu, setMenu] = useState<Maybe<{ anchor: HTMLElement; chat: ChatHistoryItem }>>(null)
  const [selected, setSelected] = useState<Maybe<{
    chat: ChatHistoryItem
    action: 'edit' | 'delete'
  }>>(null)
  const { mutate: deleteChat, isLoading } = useDeleteChatMutation({
    onSuccess: (_, { id }) => {
      setSelected(null)
      if (id === chatId) history.replace('/chat')
    },
  })
  const closeModal = () => {
    if (!isLoading) setSelected(null)
  }

  const handleSelectChat = (selectedChatId: string) => {
    history.push(`/chat/${selectedChatId}`)
    onNavigate()
  }

  return (
    <Styled.Container>
      <Styled.Title>{t('aiChat.history.title')}</Styled.Title>
      <Styled.List>
        {isSuccess && chats.length === 0 && (
          <Typography variant='body2' color='textSecondary'>
            {t('aiChat.history.empty')}
          </Typography>
        )}
        {chats.map((chat) => (
          <Styled.Card
            $selected={chat.id === chatId}
            key={chat.id}
          >
            <Styled.TitleButton type='button' onClick={() => handleSelectChat(chat.id)}>
              <Styled.CardTitle>{chat.title}</Styled.CardTitle>
            </Styled.TitleButton>
            <Styled.MoreIconButton
              size='small'
              aria-label={t('aiChat.history.actions', { title: chat.title })}
              aria-haspopup='menu'
              aria-controls={menu?.chat.id === chat.id ? 'chat-history-menu' : undefined}
              aria-expanded={menu?.chat.id === chat.id}
              onClick={(event) => setMenu({ anchor: event.currentTarget, chat })}
            >

              <Styled.MoreIcon fontSize='small' />
            </Styled.MoreIconButton>
          </Styled.Card>
        ))}
      </Styled.List>
      <Menu
        id='chat-history-menu'
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
      >
        <MenuItem onClick={() => {
          if (menu) setSelected({ chat: menu.chat, action: 'edit' })
          setMenu(null)
        }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (menu) setSelected({ chat: menu.chat, action: 'delete' })
          setMenu(null)
        }}
        >
          {t('common.delete')}
        </MenuItem>
      </Menu>
      {selected?.action === 'edit' && (
        <EditChatModal chat={selected.chat} onClose={closeModal} />
      )}
      {selected?.action === 'delete' && (
        <DeleteModal
          open
          subText={t('aiChat.history.deletePrompt', { title: selected.chat.title })}
          onClose={closeModal}
          onSave={() => {
            if (!isLoading) deleteChat({ id: selected.chat.id })
          }}
          isLoading={isLoading}
        />
      )}
    </Styled.Container>
  )
}

export { ChatHistory }
